// Imports => React
import { useCallback, useEffect, useMemo, useState } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import clsx from "clsx";

// Imports => Constants
import {
  AcCard,
  AcEmptyBlock,
  AcHeading,
  AcIsSet,
  AcTabContainer,
  AcLoaderInline,
  KEYS,
  THEMES,
  TYPES,
  AcTextInput,
  AcDivider,
} from "@opp/ui-kit";

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcRichContent,
  AcButton,
} from "@opp/ui-kit";
import { withTranslation } from "react-i18next";
import AcCopyText from "../../atoms/ac-copy-text/ac-copy-text";
import {
  AcFormatCurrencyAmount,
  AcFormatCurrencySeparator,
} from "@src/utilities";
import { useFormActions } from "@src/hooks";
import { usePostTransactionsMutation } from "@src/api/openapi/merchantApi";
import getSymbolFromCurrency from "currency-symbol-map";

const _CLASSES = {
  MAIN: "ac-add-balance-modal ac-modal--size-lg",
  CONTENT: "ac-add-balance-modal__content",
};

const AcAddBalanceModal = ({
  store: { ui, profile, balance, transactions, toasters, settings },
  t,
  i18n,
}) => {
  const {
    balance: { profile_balances },
  } = balance;

  const { configurations } = settings;
  const { current_partner_currency } = profile;
  const [selectedProfile, setSelectedProfile] = useState();

  const [fields, setFields] = useState({
    amount: 0,
    amount_decimal: 0,
    ideal_issuer: null,
  });
  const [errors, setErrors] = useState({
    amount: null,
    amount_decimal: null,
    ideal_issuer: null,
  });

  const [initiateAddBalance, { data: addBalanceData, error, isLoading }] =
    usePostTransactionsMutation();

  const { hasErrors, handleInputChange, handleInputValidation } =
    useFormActions({
      t,
      fields,
      setFields,
      errors,
      setErrors,
    });

  useEffect(() => {
    if (addBalanceData?.data?.redirect_url)
      window.location = addBalanceData.data.redirect_url;
  }, [addBalanceData]);

  useEffect(() => {
    if (error && error.status !== 200) {
      toasters.add({
        variant: "error",
        description: t("common:something-went-wrong"),
      });
    }
  }, [error]);

  useEffect(() => {
    if (!AcIsSet(profile.profiles)) {
      profile.list_profiles(t);
    }
  }, [profile.profiles]);
  const handleClose = (event) => {
    if (event && event.preventDefault) event.preventDefault();

    ui.reset(KEYS.MODAL, false);
  };

  const getContentClassNames = useMemo(() => {
    return clsx([_CLASSES.CONTENT]);
  }, []);

  const getStyleClassNames = useMemo(() => {
    return clsx([_CLASSES.MAIN]);
  }, []);

  const getCloseButtonOptions = useMemo(() => {
    return {
      type: TYPES.BUTTON,
      theme: THEMES.OMEGA,
      title: t("cancel", "Cancel", { ns: "titles" }),
      callback: handleClose,
    };
  }, [handleClose]);

  const handleSubmitTopup = useCallback(
    (event) => {
      if (event && event.preventDefault) {
        event.preventDefault();
      }
      if (event && event.persist) {
        event.persist();
      }

      let amount = fields.amount.toString();
      let amountDec = fields.amount_decimal.toString();
      if (amountDec.length === 1) {
        amountDec = amountDec.toString() + "0";
      }

      const topupData = {
        total_price: parseInt(amount.toString() + amountDec.toString()),
        profile_uid: selectedProfile,
        locale: i18n.resolvedLanguage.slice(0, 2),
        return_url:
          window.location.href.replace(window.location.hash, "") +
          "?addBalanceStatus={STATUS}",
      };
      initiateAddBalance({ body: topupData });
    },
    [profile, selectedProfile, fields.amount, fields.amount_decimal]
  );
  const getSubmitTopupButtonOptions = useMemo(() => {
    let actualDecimals = fields.amount_decimal.toString();
    if (actualDecimals.length === 1) {
      actualDecimals = actualDecimals + "0";
    }

    return {
      type: TYPES.SUBMIT,
      theme: THEMES.OMEGA,
      disabled:
        (!fields.amount && !fields.amount_decimal) ||
        fields.amount < 0 ||
        fields.amount_decimal < 0 ||
        isLoading,
      title: `${t("dashboard:top-up-balance", "Add balance")}`,
      callback: handleSubmitTopup,
      loading: isLoading,
      className: "h-margin-left-5",
    };
  }, [
    fields,
    errors,
    hasErrors,
    isLoading,
    handleSubmitTopup,
    transactions,
    transactions.is_busy,
  ]);
  const getAmountInputOptions = useMemo(() => {
    return {
      type: TYPES.NUMBER,
      name: "amount",
      value: fields.amount,
      callback: handleInputChange,
      validation: handleInputValidation,
      required: true,
      tabIndex: 0,
      focus: true,
      readonly: false,
      className: "ac-refund-modal__input-amount",
    };
  }, [fields, fields.amount]);

  const getAmountDecimalInputOptions = useMemo(() => {
    return {
      type: TYPES.NUMBER,
      name: "amount_decimal",
      value: fields.amount_decimal,
      callback: handleInputChange,
      validation: handleInputValidation,
      required: true,
      tabIndex: 1,
      focus: false,
      readonly: false,
      className: "ac-refund-modal__input-amount-decimal",
    };
  }, [fields, fields.amount_decimal]);

  const shouldRenderTopupViaTransaction = useMemo(() => {
    if (!configurations.topups) {
      return false;
    }

    if (!selectedProfile || selectedProfile === "EMPTY_OPTION") {
      return false;
    }

    return true;
  }, [selectedProfile, configurations]);

  const renderAmountInputs = useMemo(() => {
    if (!shouldRenderTopupViaTransaction) {
      return null;
    }

    return (
      <>
        <AcCard
          className={
            "ac-profile__column ac-profile__column--wide h-padding-x-0 h-padding-bottom-0 h-margin-bottom-0"
          }
          theme={THEMES.TRANSPARENT}
        >
          <AcContainer>
            <AcRow className={"h-justify-content-center h-align-items-center"}>
              <span className={"ac-refund-modal__currency-symbol"}>
                {getSymbolFromCurrency(current_partner_currency)}
              </span>

              <AcTextInput {...getAmountInputOptions} />

              <span className={"ac-refund-modal__currency-separator"}>
                {AcFormatCurrencySeparator(i18n.resolvedLanguage)}
              </span>

              <AcTextInput {...getAmountDecimalInputOptions} />
            </AcRow>
          </AcContainer>
        </AcCard>
      </>
    );
  }, [
    transactions,
    transactions.is_loading_refundable,
    fields,
    fields.amount,
    fields.amount_decimal,
    current_partner_currency,
    i18n.resolvedLanguage,
    configurations,
    selectedProfile,
  ]);

  const renderVirtualIban = (virtualIban, key) => {
    return (
      <div>
        {key !== 0 && (
          <AcRow className={"h-margin-right-10"}>
            <AcDivider
              className={"h-margin-top-10 h-margin-bottom-20"}
              theme={THEMES.EGGWHITE}
            />
          </AcRow>
        )}

        <AcRow className={key === 0 ? "h-margin-top-20" : ""}>
          <AcHeading
            rank={6}
            theme={THEMES.MEDIUM}
            className={"h-padding-y-0 h-margin-y-0"}
          >
            {t("account-holder", "Account holder", { ns: "common" })}
          </AcHeading>
          {virtualIban.account_holder && (
            <AcCopyText t={t} value={virtualIban.account_holder} fixed />
          )}
        </AcRow>
        <AcRow className={"h-margin-right-10"}>
          <AcTextInput
            type={TYPES.TEXT}
            className={"h-width-100-p"}
            label={""}
            value={
              virtualIban.account_holder
                ? virtualIban.account_holder
                : t("unknown", { ns: "common" })
            }
            disabled
          />
        </AcRow>

        <AcRow>
          <AcHeading
            rank={6}
            theme={THEMES.MEDIUM}
            className={"h-padding-y-0 h-margin-y-0"}
          >
            {t("account-number", "Account number", { ns: "common" })}
          </AcHeading>
          {virtualIban.iban && (
            <AcCopyText t={t} value={virtualIban.iban} fixed />
          )}
        </AcRow>
        <AcRow className={"h-margin-right-10"}>
          <AcTextInput
            type={TYPES.TEXT}
            className={"h-width-100-p"}
            label={""}
            value={
              virtualIban.iban
                ? virtualIban.iban
                : t("unknown", { ns: "common" })
            }
            disabled
          />
        </AcRow>

        <AcRow>
          <AcHeading
            rank={6}
            theme={THEMES.MEDIUM}
            className={"h-padding-y-0 h-margin-y-0"}
          >
            {t("bic", "BIC", { ns: "common" })}
          </AcHeading>
          {virtualIban.bic && (
            <AcCopyText t={t} value={virtualIban.bic} fixed />
          )}
        </AcRow>
        <AcRow className={"h-margin-right-10"}>
          <AcTextInput
            type={TYPES.TEXT}
            className={"h-width-100-p"}
            label={""}
            value={
              virtualIban.bic ? virtualIban.bic : t("unknown", { ns: "common" })
            }
            disabled
          />
        </AcRow>

        {virtualIban.country && (
          <>
            <AcRow>
              <AcHeading
                rank={6}
                theme={THEMES.MEDIUM}
                className={"h-padding-y-0 h-margin-y-0"}
              >
                {t("country", "Country", { ns: "common" })}
              </AcHeading>
            </AcRow>
            <AcRow className={"h-margin-right-10"}>
              <AcTextInput
                type={TYPES.TEXT}
                className={"h-width-100-p"}
                label={""}
                value={virtualIban.country}
                disabled
              />
            </AcRow>
          </>
        )}

        {virtualIban.currency && (
          <>
            <AcRow>
              <AcHeading
                rank={6}
                theme={THEMES.MEDIUM}
                className={"h-padding-y-0 h-margin-y-0"}
              >
                {t("currency", "Currency", { ns: "common" })}
              </AcHeading>
            </AcRow>
            <AcRow className={"h-margin-right-10"}>
              <AcTextInput
                type={TYPES.TEXT}
                className={"h-width-100-p"}
                label={""}
                value={virtualIban.currency}
                disabled
              />
            </AcRow>
          </>
        )}
      </div>
    );
  };

  const renderProfile = (profile) => {
    return (
      <div className={"ac-profile__column ac-profile__column--wide"}>
        <AcRow>
          <AcColumn>
            <AcCard className={"h-padding-y-0"} style={{ overflowY: "auto" }}>
              {profile.virtual_ibans.length > 0 ? (
                <>
                  {profile.virtual_ibans.map((virtualIban, key) => {
                    return renderVirtualIban(virtualIban, key);
                  })}
                </>
              ) : (
                settings.settings.has_virtual_iban && (
                  <AcEmptyBlock
                    message={t(
                      "no-vibans-found",
                      "No virtual IBANs available.",
                      {
                        ns: "common",
                      }
                    )}
                  />
                )
              )}
            </AcCard>
          </AcColumn>
        </AcRow>
      </div>
    );
  };

  const renderProfiles = useMemo(() => {
    if (!AcIsSet(profile) || profile.is_loading) {
      return <AcLoaderInline loading />;
    }
    if (!AcIsSet(profile.profiles) || profile.profiles.length === 0) {
      return (
        <AcEmptyBlock
          message={t("no-profiles-found", "No profiles found", {
            ns: "common",
          })}
        />
      );
    }

    const collection = profile.profiles.filter(
      (e) => e.status !== "terminated"
    );
    profile_balances.forEach(({ uid, amount, amount_planned }) => {
      const matchedProfile = collection.find((item) => {
        return item.uid === uid;
      });
      // no profile = removed profile with terminated state
      if (!matchedProfile) return;
      matchedProfile.amount = amount - amount_planned;
    });
    const profileWithBankAmount = collection.findIndex((profile) => {
      return profile.amount;
    });
    if (profileWithBankAmount !== -1) {
      collection.unshift(...collection.splice(profileWithBankAmount, 1));
    }
    const len = collection.length;
    let n = 0;
    let result = [];

    for (n; n < len; n++) {
      const profile = collection[n];
      const object = {
        id: profile.uid,
        label: `${profile.name}${
          profile.po_number ? ` - ${profile.po_number}` : ""
        }(${AcFormatCurrencyAmount(
          profile.amount,
          current_partner_currency,
          i18n.resolvedLanguage,
          true
        )})`,
        disabled: false,
        component: renderProfile(profile),
        allowed: null,
      };

      result.push(object);
    }
    if (profile.profiles.length > 1) {
      result.unshift({ label: "", id: "EMPTY_OPTION" });
    }

    return (
      <>
        <AcCard
          className={
            "ac-profile__column ac-profile__column--wide h-padding-x-0 h-padding-bottom-0 h-margin-bottom-0"
          }
          theme={THEMES.TRANSPARENT}
        >
          <AcContainer>
            <AcRow>
              <AcColumn className={"h-padding-x-0"}>
                <AcTabContainer
                  selectMinWidth={"25rem"}
                  disableIndicator={true}
                  collection={result}
                  callback={(profileUid) => {
                    setSelectedProfile(profileUid);
                  }}
                  data={null}
                  selectForMobile={true}
                  onlyDropdown
                  placeholder={`${t("paymentLinks:chooseProfile")}`}
                />
              </AcColumn>
            </AcRow>
          </AcContainer>
        </AcCard>
      </>
    );
  }, [profile, profile.profiles, setSelectedProfile]);

  const getIntroText = useMemo(() => {
    if (!AcIsSet(profile) || profile.is_loading) {
      return <AcLoaderInline loading />;
    }
    if (!AcIsSet(profile.profiles) || profile.profiles.length === 0) {
      return t("no-profiles-found", "No profiles found", { ns: "common" });
    }

    if (profile.profiles.length === 1) {
      return t("add-balance-intro-one-profile", { ns: "dashboard" });
    }

    return t("add-balance-intro", { ns: "dashboard" });
  }, [profile, profile.profiles, t]);

  return (
    <div className={getStyleClassNames}>
      <div className={getContentClassNames}>
        <AcContainer className={"h-padding-left-0"}>
          <AcRow>
            <AcColumn>
              <AcRichContent
                content={`<p>${getIntroText}</p>`}
                className={"h-text--color-medium"}
              />
            </AcColumn>
          </AcRow>

          {renderProfiles}

          {renderAmountInputs}

          {shouldRenderTopupViaTransaction && (
            <AcRow className={"h-margin-top-20"}>
              <AcColumn
                xs={12}
                className={"h-text--align-center h-flex-h-align-center gap-1"}
                style={{ gap: "1rem" }}
              >
                <AcButton {...getSubmitTopupButtonOptions}>
                  <span>{t("dashboard:top-up-balance")}</span>
                </AcButton>
                <AcButton {...getCloseButtonOptions}>
                  <span>{t("close", "Close", { ns: "common" })}</span>
                </AcButton>
              </AcColumn>
            </AcRow>
          )}
        </AcContainer>
      </div>
    </div>
  );
};

export default withTranslation(["dashboard", "common", "titles"])(
  withStore(observer(AcAddBalanceModal))
);
